import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { API, useAuth } from "../../context/AuthContext";

/**
 * Emergent Google Auth landing.
 * URL fragment: #session_id=xxx
 * Calls POST /api/wallet/google-callback -> either logs in or routes to GoogleContinue chooser.
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
export default function AuthCallback() {
  const { tenantSlug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { login: authLogin } = useAuth();
  const [error, setError] = useState(null);
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const fragment = location.hash || "";
    const match = fragment.match(/session_id=([^&]+)/);
    if (!match) {
      setError("No session found. Please try again.");
      return;
    }
    const sessionId = decodeURIComponent(match[1]);

    (async () => {
      try {
        const res = await axios.post(`${API}/wallet/google-callback`, { session_id: sessionId });
        const data = res.data;
        if (data.flow === "login") {
          authLogin(data.token, {
            name: data.name,
            email: data.email || "",
            customer_id: data.customer_id,
            role: data.role || "guest",
            must_change_password: data.must_change_password || false,
            pending_member_approval: data.pending_member_approval || false,
          });
          toast.success(`Welcome back, ${data.name || "there"}!`);
          // Honour ?next= that was stashed before the Google redirect.
          let next = null;
          try {
            next = sessionStorage.getItem("post_login_next");
            sessionStorage.removeItem("post_login_next");
          } catch { /* ignore */ }
          if (next && next.startsWith("/") && !next.startsWith("//")) {
            navigate(next, { replace: true });
          } else {
            navigate(`/${tenantSlug}/dashboard`, { replace: true });
          }
        } else if (data.flow === "register") {
          navigate(`/${tenantSlug}/google-continue`, {
            replace: true,
            state: { google: data.google },
          });
        } else {
          setError("Unexpected response from server.");
        }
      } catch (e) {
        setError(e.response?.data?.detail || "Google sign-in failed. Please try again.");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-white to-slate-50 px-4">
      <div className="bg-white rounded-3xl shadow-lg p-8 max-w-md w-full text-center" data-testid="auth-callback">
        {error ? (
          <>
            <p className="text-red-600 font-bold mb-3">Sign-in error</p>
            <p className="text-sm text-slate-600 mb-6">{error}</p>
            <button
              onClick={() => navigate(`/${tenantSlug}/login`)}
              className="px-6 py-2 rounded-full bg-[#1e3a5f] text-white text-sm font-medium"
            >Back to login</button>
          </>
        ) : (
          <>
            <Loader2 className="w-8 h-8 text-[#1e3a5f] animate-spin mx-auto mb-4" />
            <p className="text-slate-600">Signing you in with Google…</p>
          </>
        )}
      </div>
    </div>
  );
}
